import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { FieldDefinition, FieldType } from '@stylehaven/components';
import { TranslateOrDefaultPipe } from '../../elements/pipes/translate-or-default.pipe';
import { FormatDate } from '../../helpers/format-date.helper';
import { FieldDefinitionService } from '../../services/field-definition/field-definition.service';
import { ApiFragment } from '../entities/api-fragment.entity';
import { GetField } from '../helpers/get-field.helper';

@Pipe({
    standalone: false,
    name: 'getField',
    pure: true
})
export class GetFieldPipe implements PipeTransform {
    constructor(
        private fieldDefinitionService: FieldDefinitionService,
        private readonly translateOrDefaultPipe: TranslateOrDefaultPipe,
        private readonly datePipe: DatePipe,
        private readonly translateService: TranslateService
    ) {}

    transform(
        apiFragment: ApiFragment,
        fieldDefinition: FieldDefinition | string,
        defaultValue: string = '-',
        transformDate: boolean = true,
        ...args: string[]
    ): Observable<any> {
        return this.fieldDefinitionService.getAll().pipe(
            take(1),
            map((fieldDefinitions): [any, FieldDefinition | undefined] => [
                GetField(apiFragment, fieldDefinition),
                this.getDefinition(fieldDefinition, fieldDefinitions)
            ]),
            map(([field, definition]): [string, FieldDefinition | undefined] => [
                this.mapFieldToString(field, definition, defaultValue, transformDate),
                definition
            ]),
            map(([field, definition]) => this.translateOrGetValue(definition, field))
        );
    }

    private getDefinition(
        fieldDefinition: FieldDefinition | string,
        fieldDefinitions: FieldDefinition[]
    ): FieldDefinition | undefined {
        if ((fieldDefinition as FieldDefinition).DottedKey) {
            return fieldDefinition as FieldDefinition;
        } else {
            return fieldDefinitions.find(
                definition => definition.DottedKey === fieldDefinition || definition.FlatKey === fieldDefinition
            );
        }
    }

    private translateOrGetValue(fieldDefinition: FieldDefinition | undefined, fieldValue: string): string {
        if (this.isTranslateable(fieldDefinition)) {
            return this.translateFieldDefinitionValue(fieldDefinition, fieldValue);
        }

        return fieldValue;
    }

    private translateFieldDefinitionValue(fieldDefinition: FieldDefinition, fieldValue: string) {
        const translationQuery = `overrides.${fieldDefinition.FlatKey}.${fieldValue}`;
        return this.translateOrDefaultPipe.transform(translationQuery, fieldValue);
    }

    private isTranslateable(fieldDefinition: FieldDefinition | undefined): fieldDefinition is FieldDefinition {
        if (!fieldDefinition) {
            return false;
        }

        return (
            this.translateService.instant(`overrides.${fieldDefinition.FlatKey}`) !==
            `overrides.${fieldDefinition.FlatKey}`
        );
    }

    mapFieldToString(
        field: any,
        fieldDefinition: FieldDefinition | undefined,
        defaultValue: string,
        transformDate: boolean
    ): string {
        if (Array.isArray(field)) {
            return field
                .map(subField => this.mapFieldToString(subField, fieldDefinition, defaultValue, transformDate))
                .join(', ');
        }

        if (fieldDefinition?.Type === FieldType.DateField && !!field && transformDate) {
            return FormatDate(this.datePipe, field, undefined, defaultValue);
        } else {
            return `${field || defaultValue}`;
        }
    }
}
